import {
    AccountTree as AccountTreeIcon,
    ArrowBack as ArrowBackIcon,
    AutoAwesome as AutoAwesomeIcon,
    Close as CloseIcon,
    MenuBook as MenuBookIcon,
    UnfoldLess as UnfoldLessIcon,
    UnfoldMore as UnfoldMoreIcon,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    TextField,
    Tooltip,
    Typography,
    keyframes,
    useTheme
} from "@mui/material";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    WORD_REVEAL_BANK_BOUNCE_MS,
    wordBankLockInKeyframes,
} from "../../common/components/WordRevealFx";
import WordMeaning from "../../../../admin/pzl_common/WordMeaning";
import AuthService from "../../../../services/AuthService";
import LoginConfirmDialog from "../../../components/LoginConfirmDialog";
import { normalizeWord, removeHyphens } from "../../common/utils/PuzzlePlayLogic";
import { usePagesTranslation } from "../../../locales";
import AssistantCreditsEmptyBar from "../../common/components/AssistantCreditsEmptyBar";
import { ACCENT, SURFACE, TEXT } from "../../../../dark-theme/palette";

const hintedChipPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(249, 168, 37, 0.55);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(249, 168, 37, 0);
    transform: scale(1.04);
  }
`;

function SortedWordChips({
    words,
    wordsFound,
    clickedChips,
    hintedWordKeys,
    storyHighlightedWord,
    theme,
    onChipClick,
}) {
    const sorted = useMemo(
        () => [...words].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
        [words]
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                pt: 0.75,
                pb: 3,
                pr: 0.5,
            }}
        >
            {sorted.map((word, index) => {
                const isFound = wordsFound.includes(word.toLowerCase());
                const isHinted =
                    !isFound &&
                    hintedWordKeys instanceof Set &&
                    hintedWordKeys.has(normalizeWord(word));
                const isStoryEmphasized =
                    storyHighlightedWord != null && word === storyHighlightedWord;
                const isClicked = clickedChips.has(word);
                return (
                    <Chip
                        key={`${word}-${index}`}
                        label={word}
                        variant={isFound ? 'filled' : isHinted ? 'filled' : 'outlined'}
                        color={isFound ? 'success' : 'default'}
                        onClick={() => onChipClick(word)}
                        sx={{
                            maxWidth: 'none',
                            height: 'auto',
                            overflow: 'visible',
                            boxSizing: 'border-box',
                            border: isHinted
                                ? '2px solid #F9A825'
                                : isClicked
                                ? '2px solid #e65100'
                                : isStoryEmphasized
                                  ? `2px solid ${theme.palette.primary.main}`
                                  : undefined,
                            '& .MuiChip-label': {
                                overflow: 'visible',
                                textOverflow: 'clip',
                                whiteSpace: 'nowrap',
                                maxWidth: 'none',
                                lineHeight: 1.43,
                                fontSize:
                                    isHinted || isClicked || isStoryEmphasized ? '1rem' : '0.875rem',
                                fontWeight:
                                    isFound || isHinted || isClicked || isStoryEmphasized ? 'bold' : 'normal',
                                color: isFound ? '#333' : isHinted ? '#5D4037' : undefined,
                            },
                            backgroundColor: isHinted
                                ? '#FFE082'
                                : isStoryEmphasized && !isFound && !isClicked
                                    ? theme.palette.mode === 'dark'
                                        ? 'rgba(33, 150, 243, 0.2)'
                                        : 'rgba(33, 150, 243, 0.12)'
                                    : isFound
                                      ? '#FFEB3B'
                                      : undefined,
                            textDecoration: isFound ? 'line-through' : 'none',
                            opacity: isFound ? 0.9 : 1,
                            fontWeight:
                                isFound || isHinted || isClicked || isStoryEmphasized ? 'bold' : 'normal',
                            transition: 'border-color 0.15s ease-in-out',
                            cursor: 'pointer',
                            color: isFound ? '#333' : isHinted ? '#5D4037' : undefined,
                            animation: isHinted ? `${hintedChipPulse} 1.6s ease-in-out infinite` : undefined,
                            '&.MuiChip-filled': {
                                backgroundColor: isFound
                                    ? '#FFEB3B !important'
                                    : isHinted
                                      ? '#FFE082 !important'
                                      : undefined,
                                color: isFound
                                    ? '#333 !important'
                                    : isHinted
                                      ? '#5D4037 !important'
                                      : undefined,
                            },
                        }}
                    />
                );
            })}
        </Box>
    );
}

const WordsToFindDrawer = ({
    words = [],
    wordsFound = [],
    clickedChips = new Set(),
    hintedWordKeys = null,
    onChipClick,
    onHide,
    onNetworkToggle,
    showNetwork = false,
    hasWordNetwork = false,
    selectedNetworkWord = null,
    showWordMeaning = true,
    wordMeaningMaxHeight = null,
    skipAuthCheck = false,
    hideCloseButton = false,
    language = "english",
    chatWithAiOpen = false,
    assistantMagicVisible: assistantMagicVisibleProp,
    assistantMagicTooltipTitle,
    assistantInputLocked = false,
    assistantLockedInputText = "",
    onOpenChatWithAi,
    onOpenStory,
    /** Story accordion above the word list (from GridPlayMain). */
    storySlot = null,
    /** Pulse when a revealed word lands in the bank. */
    bankLockIn = false,
    onCloseChatWithAi = () => {},
    assistantMessages = [],
    assistantPrompt = "",
    onAssistantPromptChange = () => {},
    onAssistantSend = () => {},
    assistantLoading = false,
    assistantHistoryLoading = false,
    chatWithAiTitle = "Chat with AI",
    chatWithAiPlaceholder = "",
    assistantCreditsTotal = undefined,
    /** Puzzle clue lines `{ word, definition }[]` from GET /home/grid/:id (or template); shown above dictionary meaning. */
    displayClue = null,
    /** Puzzle word string from grid story vocab tap — chip with this label gets emphasis */
    storyHighlightedWord = null,
    /** When true, header shows a fold control to hide the meaning column so the word list expands. */
    collapsibleMeaningPanel = false,
}) => {
    const { t, lang: uiLang } = usePagesTranslation();
    const navigate = useNavigate();
    const showAssistantMagicButton =
        assistantMagicVisibleProp != null
            ? Boolean(assistantMagicVisibleProp)
            : typeof onOpenChatWithAi === "function";
    const theme = useTheme();
    const [selectedWord, setSelectedWord] = useState(null);
    const [loginDialogOpen, setLoginDialogOpen] = useState(false);
    const [meaningPanelCollapsed, setMeaningPanelCollapsed] = useState(false);

    const showSplitWordAndMeaning =
        showWordMeaning && (!collapsibleMeaningPanel || !meaningPanelCollapsed);

    const assistantCreditsDepleted =
        !assistantInputLocked &&
        typeof assistantCreditsTotal === "number" &&
        assistantCreditsTotal <= 0;

    const displayClueDefinitionByWord = useMemo(() => {
        const list = Array.isArray(displayClue) ? displayClue : [];
        const map = new Map();
        for (const entry of list) {
            const w = entry?.word;
            const def = String(entry?.definition ?? "").trim();
            if (!w || !def) continue;
            const k = normalizeWord(String(w));
            if (!k) continue;
            map.set(k, def);
            const kh = removeHyphens(k);
            if (kh && kh !== k) {
                map.set(kh, def);
            }
        }
        return map;
    }, [displayClue]);

    const resolveDisplayClueDefinition = useCallback(
        (word) => {
            if (!word) return "";
            const k = normalizeWord(word);
            return (
                displayClueDefinitionByWord.get(k) ||
                displayClueDefinitionByWord.get(removeHyphens(k)) ||
                ""
            );
        },
        [displayClueDefinitionByWord]
    );

    const handleChipClick = (word) => {
        setSelectedWord(word);
        if (onChipClick) {
            onChipClick(word);
        }
    };

    const handleNetworkToggle = async () => {
        // Skip authentication check if skipAuthCheck is true (for demo/static pages)
        if (!skipAuthCheck) {
            // Check if user is logged in
            const isAuthenticated = await AuthService.isAuthenticated();
            if (!isAuthenticated) {
                setLoginDialogOpen(true);
                return;
            }
        }

        // Proceed with network toggle
        if (onNetworkToggle) {
            onNetworkToggle();
        }
    };

    // Determine which word to show in WordMeaning
    // Show WordMeaning when network icon is clicked (showNetwork is true) and there's a selected network word
    // Or when a word chip is clicked
    const wordToShow = (showNetwork && selectedNetworkWord) ? selectedNetworkWord : selectedWord;
    const displayClueDefinition =
        wordToShow ? resolveDisplayClueDefinition(wordToShow) : "";

    return (
        <Paper
            elevation={3}
            data-word-bank
            sx={{
                width: '100%',
                flex: '1 1 0%',
                minHeight: 0,
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                ...(bankLockIn
                    ? {
                          animation: `${wordBankLockInKeyframes} ${WORD_REVEAL_BANK_BOUNCE_MS}ms ease-out`,
                      }
                    : {}),
            }}
        >
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                borderBottom: `1px solid ${SURFACE.border}`,
                gap: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
                    {chatWithAiOpen ? (
                        <>
                            <Tooltip title={t("puzzle.playCross.back-to-clues", "Back to word list")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={onCloseChatWithAi}
                                    aria-label={t("puzzle.playCross.back-to-clues", "Back to word list")}
                                    sx={{
                                        color: ACCENT.light,
                                        flexShrink: 0,
                                        '&:hover': {
                                            backgroundColor: ACCENT.soft,
                                            color: TEXT.primary,
                                        },
                                    }}
                                >
                                    <ArrowBackIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, color: ACCENT.light, fontSize: '0.95rem' }}
                            >
                                {chatWithAiTitle}
                            </Typography>
                        </>
                    ) : (
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: ACCENT.light, fontSize: '0.95rem', minWidth: 0 }}
                        >
                            {t("puzzle.playGrid.words-to-find")}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                    {!chatWithAiOpen && showAssistantMagicButton ? (
                        <Tooltip
                            title={
                                assistantMagicTooltipTitle ||
                                t(
                                    "puzzle.playCross.ai-chat-tooltip",
                                    "Open Chat with AI in the word list"
                                )
                            }
                            arrow
                        >
                            <span>
                                <IconButton
                                    onClick={() => onOpenChatWithAi?.()}
                                    size="small"
                                    aria-label={t("puzzle.playCross.chat-with-ai", "Chat with AI")}
                                    sx={{
                                        backgroundColor: "secondary.main",
                                        color: "#fff",
                                        width: 28,
                                        height: 28,
                                        minWidth: 28,
                                        minHeight: 28,
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                        "&:hover": {
                                            backgroundColor: "secondary.dark",
                                            color: "#fff",
                                        },
                                    }}
                                >
                                    <AutoAwesomeIcon sx={{ fontSize: 16, color: "#fff" }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : null}
                    {!chatWithAiOpen && typeof onOpenStory === 'function' ? (
                        <Tooltip
                            title={t("puzzle.playCross.open-story-tooltip", "Read story")}
                            arrow
                        >
                            <IconButton
                                onClick={onOpenStory}
                                size="small"
                                aria-label={t("puzzle.playCross.open-story-tooltip", "Read story")}
                                sx={{
                                    color: TEXT.secondary,
                                    flexShrink: 0,
                                    '&:hover': {
                                        backgroundColor: SURFACE.hover,
                                        color: TEXT.primary,
                                    },
                                }}
                            >
                                <MenuBookIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                    {!chatWithAiOpen && hasWordNetwork && (
                        <Tooltip title={showNetwork ? "Hide Network" : "Show Network"} arrow>
                            <IconButton
                                onClick={handleNetworkToggle}
                                size="small"
                                sx={{
                                    color: showNetwork ? ACCENT.light : TEXT.secondary,
                                    backgroundColor: showNetwork ? ACCENT.soft : 'transparent',
                                    '&:hover': {
                                        backgroundColor: showNetwork ? 'rgba(59, 166, 102, 0.28)' : SURFACE.hover,
                                        color: showNetwork ? ACCENT.light : TEXT.primary
                                    }
                                }}
                            >
                                <AccountTreeIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!chatWithAiOpen && collapsibleMeaningPanel && showWordMeaning && (
                        <Tooltip
                            title={
                                meaningPanelCollapsed
                                    ? t("puzzle.playGrid.show-meaning-panel")
                                    : t("puzzle.playGrid.hide-meaning-panel")
                            }
                            arrow
                        >
                            <IconButton
                                onClick={() => setMeaningPanelCollapsed((c) => !c)}
                                size="small"
                                aria-label={
                                    meaningPanelCollapsed
                                        ? t("puzzle.playGrid.show-meaning-panel")
                                        : t("puzzle.playGrid.hide-meaning-panel")
                                }
                                sx={{
                                    color: meaningPanelCollapsed ? ACCENT.light : TEXT.secondary,
                                    backgroundColor: meaningPanelCollapsed ? ACCENT.soft : "transparent",
                                    "&:hover": {
                                        backgroundColor: meaningPanelCollapsed
                                            ? "rgba(59, 166, 102, 0.28)"
                                            : SURFACE.hover,
                                        color: TEXT.primary,
                                    },
                                }}
                            >
                                {meaningPanelCollapsed ? (
                                    <UnfoldMoreIcon fontSize="small" />
                                ) : (
                                    <UnfoldLessIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                    {!hideCloseButton && (
                        <IconButton
                            onClick={onHide}
                            size="small"
                            sx={{
                                color: TEXT.secondary,
                                '&:hover': {
                                    backgroundColor: SURFACE.hover,
                                    color: TEXT.primary
                                }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {chatWithAiOpen ? (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            p: 1.25,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        {assistantHistoryLoading && assistantMessages.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : null}
                        {assistantMessages.map((m) => {
                            const isUser = String(m.role).toUpperCase() === 'USER';
                            return (
                                <Box
                                    key={m.id}
                                    sx={{
                                        alignSelf: isUser ? 'flex-end' : 'flex-start',
                                        maxWidth: '92%',
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: 2,
                                        bgcolor: isUser ? '#d4edda' : SURFACE.sunken,
                                        color: isUser ? '#155724' : TEXT.primary,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {m.content}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                    <Box
                        sx={{
                            flexShrink: 0,
                            p: 1.25,
                            borderTop: `1px solid ${SURFACE.border}`,
                            backgroundColor: SURFACE.sunken,
                        }}
                    >
                        {assistantCreditsDepleted ? (
                            <Box sx={{ mb: 1.25 }}>
                                <AssistantCreditsEmptyBar
                                    message={t(
                                        "puzzle.playCross.assistant-credits-empty",
                                        "You're out of Coins. Add a subscription or coin pack to keep using the assistant."
                                    )}
                                    linkLabel={t(
                                        "puzzle.playCross.assistant-credits-empty-link",
                                        "View plans and coin packs"
                                    )}
                                />
                            </Box>
                        ) : null}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-end",
                                gap: 1,
                            }}
                        >
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={5}
                                size="small"
                                placeholder={
                                    assistantInputLocked
                                        ? ""
                                        : chatWithAiPlaceholder ||
                                          t("puzzle.playCross.assistant-placeholder", "Message")
                                }
                                value={assistantInputLocked ? assistantLockedInputText : assistantPrompt}
                                onChange={(e) => {
                                    if (assistantInputLocked || assistantCreditsDepleted) return;
                                    onAssistantPromptChange(e.target.value);
                                }}
                                disabled={assistantLoading || assistantInputLocked || assistantCreditsDepleted}
                                onKeyDown={(e) => {
                                    if (assistantInputLocked || assistantCreditsDepleted) return;
                                    if (e.key !== "Enter" || e.shiftKey) return;
                                    if (e.nativeEvent.isComposing) return;
                                    e.preventDefault();
                                    if (assistantLoading || !String(assistantPrompt || "").trim()) return;
                                    onAssistantSend();
                                }}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={onAssistantSend}
                                disabled={
                                    assistantInputLocked ||
                                    assistantCreditsDepleted ||
                                    assistantLoading ||
                                    !String(assistantPrompt || "").trim()
                                }
                                sx={{
                                    flexShrink: 0,
                                    alignSelf: "flex-end",
                                    mb: "2px",
                                    textTransform: "none",
                                    backgroundColor: "#3BA666",
                                    "&:hover": { backgroundColor: "#2E8B57" },
                                    "&.Mui-disabled": {
                                        backgroundColor: "rgba(59, 166, 102, 0.45)",
                                        color: "rgba(255, 255, 255, 0.85)",
                                    },
                                }}
                            >
                                {t("puzzle.playCross.assistant-send", "Send")}
                            </Button>
                        </Box>
                        {assistantLoading || assistantHistoryLoading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: 1,
                                    mt: 0.75,
                                }}
                            >
                                <CircularProgress size={22} />
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            ) : (
            <Box
                sx={{
                    flex: '1 1 0%',
                    minHeight: 0,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
            {storySlot ? (
                <Box
                    sx={{
                        flexShrink: 0,
                        px: 1,
                        pt: 1,
                        pb: 0,
                        minWidth: 0,
                        "& .MuiAccordionDetails-root": {
                            maxHeight: { xs: 140, md: 200 },
                            overflowY: "auto",
                        },
                    }}
                >
                    {storySlot}
                </Box>
            ) : null}
            {/* Word list + meaning: left height follows chips; right caps to panel and scrolls (no forced equal column height). */}
            {showSplitWordAndMeaning ? (
                <Box
                    sx={{
                        flex: '1 1 0%',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'stretch',
                        gap: 2,
                        overflow: 'hidden',
                        m: 2,
                        mt: storySlot ? 1 : 2,
                        maxHeight: wordMeaningMaxHeight || 'none',
                    }}
                >
                    <Box
                        sx={{
                            flex: '1 1 55%',
                            minWidth: 200,
                            minHeight: 0,
                            alignSelf: 'stretch',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: SURFACE.sunken,
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    p: 2,
                                    overflowY: 'auto',
                                    overflowX: 'auto',
                                    scrollbarGutter: 'stable',
                                }}
                            >
                                <SortedWordChips
                                    words={words}
                                    wordsFound={wordsFound}
                                    clickedChips={clickedChips}
                                    hintedWordKeys={hintedWordKeys}
                                    storyHighlightedWord={storyHighlightedWord}
                                    theme={theme}
                                    onChipClick={handleChipClick}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            flex: '1 1 45%',
                            minWidth: 0,
                            minHeight: 0,
                            alignSelf: 'stretch',
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                flex: '1 1 0%',
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: SURFACE.sunken,
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            {wordToShow ? (
                                <Box
                                    sx={{
                                        flex: '1 1 0%',
                                        minHeight: 0,
                                        p: 2,
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        scrollbarGutter: 'stable',
                                    }}
                                >
                                    {displayClueDefinition ? (
                                        <Box sx={{ pb: 1.5 }}>
                                            <Typography
                                                component="div"
                                                variant="body2"
                                                sx={{
                                                    lineHeight: 1.45,
                                                    wordBreak: 'break-word',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 0.5,
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        color: ACCENT.light,
                                                        fontWeight: 600,
                                                        fontStyle: 'normal',
                                                    }}
                                                >
                                                    {t("puzzle.playGrid.hint-label", "Our Definition")}
                                                </Box>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    {displayClueDefinition}
                                                </Box>
                                            </Typography>
                                        </Box>
                                    ) : null}
                                    {displayClueDefinition ? (
                                        <Divider sx={{ mb: 1.5 }} />
                                    ) : null}
                                    <Box sx={{ minWidth: 0 }}>
                                        <WordMeaning
                                            selectedWord={wordToShow}
                                            onSaveDefinition={null}
                                            showClueButton={false}
                                            showBannedButton={false}
                                            fullHeight={false}
                                            scrollInParent
                                            scrollInParentUnpadded
                                            language={language}
                                            showPronunciationAudio={true}
                                            uiLang={uiLang}
                                            showTranslateButton={false}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 2,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        {t(
                                            'puzzle.playGrid.words-to-find-meaning-placeholder',
                                            'Tap a word on the left to see its meaning.'
                                        )}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        m: 2,
                        mt: storySlot ? 1 : 2,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: SURFACE.sunken,
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                p: 2,
                                overflowY: 'auto',
                                overflowX: 'auto',
                                scrollbarGutter: 'stable',
                            }}
                        >
                            <SortedWordChips
                                words={words}
                                wordsFound={wordsFound}
                                clickedChips={clickedChips}
                                hintedWordKeys={hintedWordKeys}
                                storyHighlightedWord={storyHighlightedWord}
                                theme={theme}
                                onChipClick={handleChipClick}
                            />
                        </Box>
                    </Box>
                </Box>
            )}
            </Box>
            )}

            {/* Login Confirmation Dialog - Only show if auth check is not skipped */}
            {!skipAuthCheck && (
                <LoginConfirmDialog
                    open={loginDialogOpen}
                    onClose={() => setLoginDialogOpen(false)}
                    onConfirm={() => {
                        navigate('/login');
                    }}
                    title={t("puzzle.playGrid.login-required")}
                    message={t("puzzle.playGrid.login-network-msg")}
                />
            )}
        </Paper>
    );
};

export default WordsToFindDrawer;
